// Utility function to add hyperlinks to fabric collection mentions in blog content

// Collection data mapping - extracted from the provided JSON
const COLLECTION_MAPPINGS = {
  // Collection names to slugs mapping
  SUNRISE: "sunrise",
  ZODIAC: "zodiac",
  MIST: "mist",
  "METALLIC SATINS": "metallic-satins",
  MAGNOLIA: "magnolia",
  ZEN: "zen",
  MAYFAIR: "mayfair",
  "MINIMALIST WIDE": "minimalist-wide",
  "BUDGET JACQUARDS": "budget-jacquards",
  CHECKMATE: "checkmate",
  "HERITAGE PRINTS": "heritage-prints",
  "DIMOUT BLOOM": "dimout-bloom",
  LUSTRE: "lustre",
  RUSSEL: "russel",
  "ECO BOUCLE SHEERS": "eco-boucle-sheers",
  "MODERN TEXTURES": "modern-textures",
  LUNAR: "lunar",
  OMEGA: "omega",
  "NATURAL JACQUARDS": "natural-jacquards",
  MAJESTIC: "majestic",
  "HOUSE BOUCLES": "house-boucles",
  VERANDA: "veranda",
  "MATTE JACQUARDS III": "matte-jacquards-iii",
  "ECO DIMOUT TEXTURES": "eco-dimout-textures",
  "THE WHITE WINDOW": "the-white-window",
  RITZ: "ritz",
  "TUNDRA BOUCLE": "tundra-boucle",
  "SERENE SHEERS": "serene-sheers",
  ROSEWOOD: "rosewood",
  SERAI: "serai",
  SAXON: "saxon",
  SATINA: "satina",
  RAFFLES: "raffles",
  "NATURAL ESSENCE": "natural-essence",
  MYTH: "myth",
  MYSTIQUE: "mystique",
  "MICHELIN WIDE": "michelin-wide",
  MELANGE: "melange",
  "MATTE JACQUARDS II": "matte-jacquards-ii",
  "MATTE JACQUARDS I": "matte-jacquards-i",
  "LUXE BOUCLE": "luxe-boucle",
  LUMINARA: "luminara",
  LOCOMOTION: "locomotion",
  KNOCKOUT: "knockout",
  "GRAPHIC TEXTURES": "graphic-textures",
  "ESPERANCE WIDE": "esperance-wide",
  "EDITION - II": "edition-ii",
  "EDITION - I": "edition-i",
  EARTHWILD: "earthwild",
  EARTHEN: "earthen",
  DAYDREAM: "daydream",
  "BUDGET BLACKOUT": "budget-blackout",
  FABLES: "fables",
};

// Alternative names and variations for collections
const COLLECTION_VARIATIONS = {
  "Eco Dim-Out Features": "eco-dimout-textures",
  "Eco Dimout Textures": "eco-dimout-textures",
  "Eco Dim-out Texture Collection": "eco-dimout-textures",
  Daydream: "daydream",
  Knockout: "knockout",
  "Budget Blackout": "budget-blackout",
  Locomotion: "locomotion",
  Earthen: "earthen",
  Satina: "satina",
  Myth: "myth",
  Raffles: "raffles",
  Saxon: "saxon",
  Melange: "melange",
  "Luxe Boucle": "luxe-boucle",
  "Tundra Boucle": "tundra-boucle",
  Mystique: "mystique",
  "Serene Sheers": "serene-sheers",
  "Natural Essence": "natural-essence",
  Rosewood: "rosewood",
  "The White Window": "the-white-window",
  Ritz: "ritz",
  Serai: "serai",
  Luminara: "luminara",
  "Graphic Textures": "graphic-textures",
  "Esperance Wide": "esperance-wide",
  "Edition - I": "edition-i",
  "Edition - II": "edition-ii",
  Earthwild: "earthwild",
  "Matte Jacquards I": "matte-jacquards-i",
  "Matte Jacquards II": "matte-jacquards-ii",
  "Matte Jacquards III": "matte-jacquards-iii",
  "Michelin Wide": "michelin-wide",
  "House Boucles": "house-boucles",
  Majestic: "majestic",
  "Natural Jacquards": "natural-jacquards",
  Omega: "omega",
  Lunar: "lunar",
  "Modern Textures": "modern-textures",
  "Eco Boucle Sheers": "eco-boucle-sheers",
  Russel: "russel",
  Lustre: "lustre",
  "Dimout Bloom": "dimout-bloom",
  "Heritage Prints": "heritage-prints",
  Checkmate: "checkmate",
  "Budget Jacquards": "budget-jacquards",
  "Minimalist Wide": "minimalist-wide",
  Mayfair: "mayfair",
  Zen: "zen",
  Magnolia: "magnolia",
  "Metallic Satins": "metallic-satins",
  Mist: "mist",
  Zodiac: "zodiac",
  Sunrise: "sunrise",
  Veranda: "veranda",
  Fables: "fables",
};

// Combine all mappings
const ALL_COLLECTIONS = { ...COLLECTION_MAPPINGS, ...COLLECTION_VARIATIONS };

/**
 * Escapes special regex characters in a string
 * @param {string} string - String to escape
 * @returns {string} - Escaped string
 */
const escapeRegex = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

/**
 * Converts collection names to hyperlinks in blog content
 * @param {string} content - The blog content text
 * @param {string} className - CSS class to apply to the links
 * @returns {string} - Content with hyperlinks added
 */
export const addCollectionHyperlinks = (
  content,
  className = "blog-collection-link"
) => {
  if (!content || typeof content !== "string") {
    return content;
  }

  // First, protect existing links by replacing them with placeholders
  const existingLinks = [];
  let protectedContent = content.replace(/<a[^>]*>.*?<\/a>/gi, (match) => {
    const placeholder = `__EXISTING_LINK_${existingLinks.length}__`;
    existingLinks.push(match);
    return placeholder;
  });

  // Sort collection names by length (longest first) to avoid partial matches
  const sortedCollections = Object.keys(ALL_COLLECTIONS).sort(
    (a, b) => b.length - a.length
  );

  // Process each collection name
  sortedCollections.forEach((collectionName) => {
    const slug = ALL_COLLECTIONS[collectionName];
    const escapedName = escapeRegex(collectionName);

    // Create a single comprehensive pattern that handles both cases
    // Match "Collection Name" optionally followed by " Collection(s)" or " collection(s)"
    const pattern = new RegExp(
      `\\b(${escapedName})(?:\\s+(Collections?|collections?))?\\b`,
      "gi"
    );

    protectedContent = protectedContent.replace(
      pattern,
      (match, collectionName, collectionSuffix) => {
        // Skip if this is inside a placeholder (existing link)
        if (match.includes("__EXISTING_LINK_")) {
          return match;
        }

        // Skip if already wrapped in a link tag
        const beforeMatch = protectedContent.substring(
          0,
          protectedContent.indexOf(match)
        );
        const afterMatch = protectedContent.substring(
          protectedContent.indexOf(match) + match.length
        );

        if (
          beforeMatch.lastIndexOf("<a") > beforeMatch.lastIndexOf("</a>") ||
          afterMatch.indexOf("</a>") < afterMatch.indexOf("<a")
        ) {
          return match;
        }

        const href = `/collection-detail/${slug}`;
        return `<a href="${href.toLowerCase()}" class="${className}" data-collection="${slug}">${match}</a>`;
      }
    );
  });

  // Restore existing links
  existingLinks.forEach((link, index) => {
    const placeholder = `__EXISTING_LINK_${index}__`;
    protectedContent = protectedContent.replace(placeholder, link);
  });

  return protectedContent;
};

/**
 * Processes React JSX content and adds hyperlinks
 * @param {string} content - JSX content as string
 * @param {string} className - CSS class for links
 * @returns {object} - Object with __html property for dangerouslySetInnerHTML
 */
export const processJSXContent = (
  content,
  className = "blog-collection-link"
) => {
  const processedContent = addCollectionHyperlinks(content, className);
  return { __html: processedContent };
};

/**
 * Gets collection slug from collection name
 * @param {string} collectionName - Name of the collection
 * @returns {string|null} - Collection slug or null if not found
 */
export const getCollectionSlug = (collectionName) => {
  return ALL_COLLECTIONS[collectionName] || null;
};

/**
 * Checks if a string contains any collection names
 * @param {string} content - Content to check
 * @returns {Array} - Array of found collection names
 */
export const findCollectionMentions = (content) => {
  if (!content || typeof content !== "string") {
    return [];
  }

  const found = [];
  const sortedCollections = Object.keys(ALL_COLLECTIONS).sort(
    (a, b) => b.length - a.length
  );

  sortedCollections.forEach((collectionName) => {
    const pattern = new RegExp(`\\b${escapeRegex(collectionName)}\\b`, "gi");
    if (pattern.test(content)) {
      found.push(collectionName);
    }
  });

  return found;
};

const blogHyperlinks = {
  addCollectionHyperlinks,
  processJSXContent,
  getCollectionSlug,
  findCollectionMentions,
  COLLECTION_MAPPINGS,
  ALL_COLLECTIONS,
};

export default blogHyperlinks;
